/**
 * StageTrackerTab — Admin Project Detail
 * 
 * Stage pe click karo → us stage ke saare items dikhenge:
 *   proto_code, status, progress, uploaded photos
 * 
 * Layout:
 *   Left panel  — stage list (clickable pills)
 *   Right panel — selected stage detail (items + images)
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import {
  Camera, CheckCircle, Clock, AlertTriangle,
  Package, ChevronRight, Loader2, ZoomIn, X,
  BarChart3, Image as ImageIcon, Box, Truck
} from 'lucide-react';
import { format, parseISO, isValid, isPast } from 'date-fns';

const safeDate = (d) => {
  try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy') : '-'; }
  catch { return '-'; }
};

const STATUS_CFG = {
  pending:     { label: 'Pending',     bg: 'bg-gray-100',    text: 'text-gray-500',   bar: 'bg-gray-300',    ring: 'ring-gray-200'   },
  in_progress: { label: 'In Progress', bg: 'bg-blue-100',    text: 'text-blue-700',   bar: 'bg-blue-500',    ring: 'ring-blue-200'   },
  completed:   { label: 'Completed',   bg: 'bg-emerald-100', text: 'text-emerald-700',bar: 'bg-emerald-500', ring: 'ring-emerald-200' },
  on_hold:     { label: 'On Hold',     bg: 'bg-amber-100',   text: 'text-amber-700',  bar: 'bg-amber-500',   ring: 'ring-amber-200'  },
  waiting:     { label: 'Waiting',     bg: 'bg-purple-100',  text: 'text-purple-700', bar: 'bg-purple-400',  ring: 'ring-purple-200' },
};

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ img, all, onClose }) {
  const [cur, setCur] = useState(img);
  const idx = all.findIndex(i => i.id === cur.id);

  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCur(all[Math.min(idx + 1, all.length - 1)]);
      if (e.key === 'ArrowLeft')  setCur(all[Math.max(idx - 1, 0)]);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [idx, all, onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex flex-col items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Counter */}
      <p className="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-xs font-medium tracking-widest uppercase">
        {idx + 1} / {all.length}
      </p>

      {/* Image */}
      <div className="max-w-4xl w-full flex items-center gap-4" onClick={e => e.stopPropagation()}>
        <button
          onClick={() => idx > 0 && setCur(all[idx - 1])}
          disabled={idx === 0}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-20 flex items-center justify-center text-white flex-shrink-0 transition-colors"
        >
          ‹
        </button>
        <img
          src={cur.image_url}
          alt={cur.caption || 'Work photo'}
          className="flex-1 max-h-[78vh] object-contain rounded-2xl shadow-2xl"
        />
        <button
          onClick={() => idx < all.length - 1 && setCur(all[idx + 1])}
          disabled={idx === all.length - 1}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-20 flex items-center justify-center text-white flex-shrink-0 transition-colors"
        >
          ›
        </button>
      </div>

      {/* Caption + uploader */}
      <div className="mt-4 text-center">
        {cur.caption && <p className="text-white/80 text-sm mb-1">{cur.caption}</p>}
        {cur.uploaded_by_name && (
          <p className="text-white/40 text-xs">📸 {cur.uploaded_by_name} · {safeDate(cur.created_at)}</p>
        )}
      </div>
    </div>
  );
}

// ── Outsource Detail Popup ────────────────────────────────────────────────────
function OutsourcePopup({ jobs, onClose }) {
  if (!jobs || jobs.length === 0) return null;
  const statusColors = {
    Sent:      'bg-blue-100 text-blue-700',
    Partial:   'bg-purple-100 text-purple-700',
    Received:  'bg-green-100 text-green-700',
    Cancelled: 'bg-gray-100 text-gray-500',
  };
  const safeD = (d) => { try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return '—'; } };
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-[150] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-100 rounded-xl flex items-center justify-center">
              <Truck className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Outsource Details</h3>
              <p className="text-xs text-gray-400">{jobs.length} job{jobs.length > 1 ? 's' : ''} for this item in this stage</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl hover:bg-gray-100 flex items-center justify-center text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Jobs */}
        <div className="p-4 space-y-3">
          {jobs.map(j => {
            const isOverdue = j.expected_date && j.expected_date < today && !['Received','Cancelled'].includes(j.status);
            const receivedPct = j.qty_sent > 0 ? Math.round((j.qty_received / j.qty_sent) * 100) : 0;
            return (
              <div key={j.id} className={`rounded-xl border p-4 ${isOverdue ? 'border-red-200 bg-red-50/40' : 'border-gray-200 bg-gray-50'}`}>
                {/* Vendor + Status */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <p className="font-bold text-gray-900">{j.vendor_name}</p>
                    {j.vendor_phone && <p className="text-xs text-gray-400 mt-0.5">📞 {j.vendor_phone}</p>}
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold shrink-0 ${statusColors[j.status] || 'bg-gray-100 text-gray-600'}`}>
                    {j.status}
                  </span>
                </div>

                {/* Qty Progress */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Qty Received</span>
                    <span className="font-semibold">{j.qty_received} / {j.qty_sent}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${j.status === 'Received' ? 'bg-green-500' : j.qty_received > 0 ? 'bg-purple-500' : 'bg-blue-400'}`}
                      style={{ width: `${receivedPct}%` }}
                    />
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-white rounded-lg p-2 border border-gray-100">
                    <p className="text-xs text-gray-400">Sent</p>
                    <p className="text-xs font-semibold text-gray-700 mt-0.5">{safeD(j.sent_date)}</p>
                  </div>
                  <div className={`rounded-lg p-2 border ${isOverdue ? 'bg-red-100 border-red-200' : 'bg-white border-gray-100'}`}>
                    <p className={`text-xs ${isOverdue ? 'text-red-500' : 'text-gray-400'}`}>Expected</p>
                    <p className={`text-xs font-semibold mt-0.5 ${isOverdue ? 'text-red-700' : 'text-gray-700'}`}>
                      {safeD(j.expected_date)}{isOverdue ? ' ⚠' : ''}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-2 border border-gray-100">
                    <p className="text-xs text-gray-400">Received</p>
                    <p className="text-xs font-semibold text-gray-700 mt-0.5">{safeD(j.received_date)}</p>
                  </div>
                </div>

                {/* Notes */}
                {j.notes && (
                  <p className="text-xs text-gray-500 mt-3 bg-white rounded-lg border border-gray-100 px-3 py-2">
                    📝 {j.notes}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Single Item Row inside a Stage ────────────────────────────────────────────
function StageItemRow({ item, task, images, onImageClick, outsourceJobs }) {
  const [imgOpen, setImgOpen] = useState(false);
  const [showOutsource, setShowOutsource] = useState(false);
  const st = STATUS_CFG[task?.status] || STATUS_CFG.pending;
  const pct = task && task.quantity_assigned > 0
    ? Math.round((task.quantity_completed / task.quantity_assigned) * 100)
    : 0;
  const isCompleted = task?.status === 'completed';
  const isActive    = task?.status === 'in_progress';
  const isDelayed   = task && task.status !== 'completed' && task.due_date && isPast(parseISO(task.due_date));
  const hasImages   = images.length > 0;

  // Outsource info for this item+stage
  const hasOutsource = outsourceJobs && outsourceJobs.length > 0;
  const outsourceOverdue = hasOutsource && outsourceJobs.some(j => {
    const today = new Date().toISOString().split('T')[0];
    return j.expected_date && j.expected_date < today && !['Received','Cancelled'].includes(j.status);
  });
  const allReceived = hasOutsource && outsourceJobs.every(j => j.status === 'Received');

  return (
    <div className={`rounded-2xl border transition-all ${
      isCompleted ? 'bg-emerald-50 border-emerald-200' :
      isActive    ? 'bg-blue-50 border-blue-200' :
      isDelayed   ? 'bg-red-50 border-red-200' :
                    'bg-white border-gray-200'
    }`}>
      {/* Main row */}
      <div className="flex items-center gap-4 p-4">
        {/* Status dot */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${st.bg}`}>
          {isCompleted
            ? <CheckCircle className={`w-5 h-5 ${st.text}`} />
            : isActive
            ? <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
            : <Clock className={`w-4.5 h-4.5 ${st.text}`} />
          }
        </div>

        {/* Item info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-bold text-gray-900">{item.item_name}</span>
            {item.proto_code && (
              <span className="text-xs font-mono bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-lg shadow-sm">
                {item.proto_code}
              </span>
            )}
            {item.material && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                {item.material}
              </span>
            )}
            {/* Outsource badge */}
            {hasOutsource && (
              <button
                onClick={() => setShowOutsource(true)}
                className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold transition-all hover:scale-105 ${
                  outsourceOverdue
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : allReceived
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                }`}
                title="Click to view outsource details"
              >
                <Truck className="w-3 h-3" />
                {outsourceOverdue ? 'Outsourced ⚠' : allReceived ? 'Outsourced ✓' : 'Outsourced'}
              </button>
            )}
          </div>

          {/* Progress */}
          {task ? (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-[160px]">
                <div className="flex-1 bg-white rounded-full h-2 border border-gray-200">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${st.bar}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className={`text-xs font-bold whitespace-nowrap ${st.text}`}>{pct}%</span>
              </div>
              <span className="text-xs text-gray-500">
                {task.quantity_completed || 0}/{task.quantity_assigned} {item.unit || 'units'}
              </span>
              {task.due_date && (
                <span className={`text-xs flex items-center gap-1 ${isDelayed ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                  {isDelayed ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                  {isDelayed ? 'Overdue: ' : 'Due: '}{safeDate(task.due_date)}
                </span>
              )}
              {task.completed_date && (
                <span className="text-xs text-emerald-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Done: {safeDate(task.completed_date)}
                </span>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400 italic">Not assigned to this stage yet</span>
          )}
        </div>

        {/* Right: status badge + image toggle + outsource button */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${st.bg} ${st.text}`}>
            {st.label}
          </span>
          {hasOutsource && (
            <button
              onClick={() => setShowOutsource(true)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-xl font-medium transition-all ${
                outsourceOverdue
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : allReceived
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              }`}
              title="View outsource details"
            >
              <Truck className="w-3.5 h-3.5" />
            </button>
          )}
          {hasImages && (
            <button
              onClick={() => setImgOpen(o => !o)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-xl font-medium transition-all ${
                imgOpen
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
              }`}
            >
              <Camera className="w-3.5 h-3.5" />
              {images.length}
            </button>
          )}
        </div>
      </div>

      {/* Outsource Popup */}
      {showOutsource && (
        <OutsourcePopup jobs={outsourceJobs} onClose={() => setShowOutsource(false)} />
      )}

      {/* Images strip */}
      {hasImages && imgOpen && (
        <div className="px-4 pb-4 pt-0">
          <div className="border-t border-dashed border-gray-200 pt-3">
            <div className="flex gap-2 flex-wrap">
              {images.map((img, i) => (
                <button
                  key={img.id || i}
                  onClick={() => onImageClick(img, images)}
                  className="relative w-20 h-20 rounded-xl overflow-hidden border-2 border-white shadow-md hover:shadow-lg group transition-all hover:scale-105 flex-shrink-0"
                >
                  <img
                    src={img.image_url}
                    alt={img.caption || 'Work photo'}
                    className="w-full h-full object-cover"
                    onError={e => { e.target.parentElement.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
            {images[0]?.uploaded_by_name && (
              <p className="text-xs text-gray-400 mt-2">
                📸 Photos by workers in this stage
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main StageTrackerTab ───────────────────────────────────────────────────────
export default function StageTrackerTab({ projectId, items, tasks, chains, outsourceJobs }) {
  const [imageData, setImageData]     = useState([]);  // [{item_id, item_name, proto_code, stages:[{stage_order,dept_name,dept_color,images:[]}]}]
  const [loadingImgs, setLoadingImgs] = useState(false);
  const [imgLoaded, setImgLoaded]     = useState(false);
  const [selectedStage, setSelectedStage] = useState(null); // {stage_order, dept_name, dept_color, department_id}
  const [lightbox, setLightbox]       = useState(null); // {img, all}

  // Build unique stages from chains (sorted by stage_order)
  const allStages = React.useMemo(() => {
    const map = {};
    (chains || []).forEach(c => {
      const key = `${c.stage_order}_${c.department_id}`;
      if (!map[key]) map[key] = {
        stage_order:   c.stage_order,
        dept_name:     c.dept_name,
        dept_color:    c.dept_color,
        department_id: c.department_id,
        key
      };
    });
    return Object.values(map).sort((a, b) => a.stage_order - b.stage_order);
  }, [chains]);

  // Auto-select first stage
  useEffect(() => {
    if (allStages.length > 0 && !selectedStage) {
      setSelectedStage(allStages[0]);
    }
  }, [allStages]);

  // Load images once
  useEffect(() => {
    if (imgLoaded) return;
    setLoadingImgs(true);
    api.get(`/projects/${projectId}/images-by-stage`)
      .then(r => { setImageData(r.data); setImgLoaded(true); })
      .catch(() => {})
      .finally(() => setLoadingImgs(false));
  }, [projectId]);

  // Build image lookup: itemId_stageOrder_deptId → [imgs]
  const imageMap = React.useMemo(() => {
    const m = {};
    imageData.forEach(itemGroup => {
      itemGroup.stages.forEach(stage => {
        const key = `${itemGroup.item_id}_${stage.stage_order}_${stage.dept_name}`;
        m[key] = stage.images || [];
      });
    });
    return m;
  }, [imageData]);

  // Stats for selected stage
  const stageStats = React.useMemo(() => {
    if (!selectedStage) return null;
    const stageTasks = tasks.filter(t =>
      Number(t.stage_order) === Number(selectedStage.stage_order) &&
      Number(t.department_id) === Number(selectedStage.department_id)
    );
    const total     = stageTasks.length;
    const completed = stageTasks.filter(t => t.status === 'completed').length;
    const inprog    = stageTasks.filter(t => t.status === 'in_progress').length;
    const delayed   = stageTasks.filter(t => t.status !== 'completed' && t.due_date && isPast(parseISO(t.due_date))).length;

    // Images count for this stage across all items
    let totalImgs = 0;
    imageData.forEach(itemGroup => {
      const s = itemGroup.stages.find(s =>
        Number(s.stage_order) === Number(selectedStage.stage_order) &&
        s.dept_name === selectedStage.dept_name
      );
      if (s) totalImgs += s.images.length;
    });

    const overallPct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, inprog, delayed, overallPct, totalImgs };
  }, [selectedStage, tasks, imageData]);

  if (allStages.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-16 text-center">
        <Box className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 font-semibold">No Production Chain Set</p>
        <p className="text-sm text-gray-400 mt-1">Set a chain for items to see the stage tracker.</p>
      </div>
    );
  }

  const selectedItems = items.map(item => {
    const task = tasks.find(t =>
      t.project_item_id === item.id &&
      Number(t.stage_order) === Number(selectedStage?.stage_order) &&
      Number(t.department_id) === Number(selectedStage?.department_id)
    );
    const imgKey = `${item.id}_${selectedStage?.stage_order}_${selectedStage?.dept_name}`;
    const images = imageMap[imgKey] || [];
    // Match outsource jobs: same item + same department as selected stage
    const itemOutsourceJobs = (outsourceJobs || []).filter(j =>
      j.project_item_id === item.id &&
      j.department === selectedStage?.dept_name
    );
    return { item, task, images, itemOutsourceJobs };
  });

  // Collect all images for this stage (for lightbox prev/next across items)
  const allStageImages = selectedItems.flatMap(({ images }) => images);

  return (
    <>
      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          img={lightbox.img}
          all={lightbox.all}
          onClose={() => setLightbox(null)}
        />
      )}

      <div className="flex flex-col md:flex-row gap-4 md:min-h-[520px]">

        {/* ── Left: Stage List ────────────────────────────────────── */}
        <div className="w-full md:w-52 md:flex-shrink-0">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden md:sticky md:top-4">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Stages</p>
              <p className="text-xs text-gray-400 mt-0.5">{allStages.length} total</p>
            </div>
            <div className="divide-y divide-gray-100">
              {allStages.map(stage => {
                const stageTasks = tasks.filter(t =>
                  Number(t.stage_order) === Number(stage.stage_order) &&
                  Number(t.department_id) === Number(stage.department_id)
                );
                const done    = stageTasks.filter(t => t.status === 'completed').length;
                const total   = stageTasks.length;
                const pct     = total > 0 ? Math.round(done / total * 100) : 0;
                const isDelayed = stageTasks.some(t =>
                  t.status !== 'completed' && t.due_date && isPast(parseISO(t.due_date))
                );
                const isSelected = selectedStage?.key === stage.key;

                // Outsource jobs for this stage (match by department name)
                const stageOutsourceJobs = (outsourceJobs || []).filter(j =>
                  j.department === stage.dept_name
                );
                const hasStageOutsource = stageOutsourceJobs.length > 0;
                const stageOutsourceOverdue = hasStageOutsource && stageOutsourceJobs.some(j => {
                  const today = new Date().toISOString().split('T')[0];
                  return j.expected_date && j.expected_date < today && !['Received','Cancelled'].includes(j.status);
                });

                return (
                  <button
                    key={stage.key}
                    onClick={() => setSelectedStage(stage)}
                    className={`w-full text-left px-4 py-3 transition-all relative ${
                      isSelected ? 'bg-slate-900 text-white' : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {/* Active indicator bar */}
                    {isSelected && (
                      <span
                        className="absolute left-0 top-1 bottom-1 w-1 rounded-r-full"
                        style={{ backgroundColor: stage.dept_color || '#f59e0b' }}
                      />
                    )}

                    <div className="flex items-center gap-2 mb-1.5">
                      <span
                        className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                          isSelected ? 'bg-white/20 text-white' : 'text-white'
                        }`}
                        style={{ backgroundColor: isSelected ? undefined : (stage.dept_color || '#9CA3AF') }}
                      >
                        {stage.stage_order}
                      </span>
                      <span className={`text-xs font-semibold truncate ${isSelected ? 'text-white' : 'text-gray-800'}`}>
                        {stage.dept_name}
                      </span>
                      {isDelayed && <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" />}
                      {hasStageOutsource && (
                        <Truck className={`w-3 h-3 flex-shrink-0 ${
                          stageOutsourceOverdue ? 'text-red-400' : isSelected ? 'text-amber-300' : 'text-amber-500'
                        }`} title="This stage has outsource jobs" />
                      )}
                    </div>

                    {/* Mini progress */}
                    <div className="flex items-center gap-2 pl-8">
                      <div className={`flex-1 h-1.5 rounded-full ${isSelected ? 'bg-white/20' : 'bg-gray-200'}`}>
                        <div
                          className="h-1.5 rounded-full transition-all"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: pct === 100 ? '#34d399' : (stage.dept_color || '#f59e0b')
                          }}
                        />
                      </div>
                      <span className={`text-xs font-bold whitespace-nowrap ${isSelected ? 'text-white/70' : 'text-gray-400'}`}>
                        {pct}%
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Right: Stage Detail ─────────────────────────────────── */}
        <div className="flex-1 min-w-0 space-y-4">

          {/* Stage header card */}
          {selectedStage && stageStats && (
            <div
              className="rounded-2xl p-5 text-white shadow-lg"
              style={{
                background: `linear-gradient(135deg, ${selectedStage.dept_color || '#1e293b'}dd, ${selectedStage.dept_color || '#334155'}99)`
              }}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      Stage {selectedStage.stage_order}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold">{selectedStage.dept_name}</h2>
                  <p className="text-white/60 text-sm mt-0.5">{items.length} items in this stage</p>
                </div>

                {/* Stats */}
                <div className="flex gap-4 flex-wrap">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{stageStats.overallPct}%</p>
                    <p className="text-white/60 text-xs">Complete</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-300">{stageStats.completed}</p>
                    <p className="text-white/60 text-xs">Done</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-300">{stageStats.inprog}</p>
                    <p className="text-white/60 text-xs">In Progress</p>
                  </div>
                  {stageStats.delayed > 0 && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-300">{stageStats.delayed}</p>
                      <p className="text-white/60 text-xs">Delayed</p>
                    </div>
                  )}
                  {stageStats.totalImgs > 0 && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-yellow-300">{stageStats.totalImgs}</p>
                      <p className="text-white/60 text-xs">Photos</p>
                    </div>
                  )}
                  {(() => {
                    const stageOutsourceCount = (outsourceJobs || []).filter(j => j.department === selectedStage?.dept_name).length;
                    return stageOutsourceCount > 0 ? (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-amber-300">{stageOutsourceCount}</p>
                        <p className="text-white/60 text-xs">Outsourced</p>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* Overall progress bar */}
              <div className="mt-4">
                <div className="w-full bg-white/20 rounded-full h-2.5">
                  <div
                    className="h-2.5 rounded-full bg-white transition-all duration-700"
                    style={{ width: `${stageStats.overallPct}%` }}
                  />
                </div>
                <p className="text-white/50 text-xs mt-1.5">
                  {stageStats.completed}/{stageStats.total} items completed in this stage
                </p>
              </div>
            </div>
          )}

          {/* Loading images notice */}
          {loadingImgs && (
            <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-200">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading work photos...
            </div>
          )}

          {/* Photo summary bar */}
          {!loadingImgs && allStageImages.length > 0 && (
            <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-2.5">
              <Camera className="w-4 h-4 text-indigo-500" />
              <span className="text-sm text-indigo-700 font-medium">
                {allStageImages.length} work photo{allStageImages.length !== 1 ? 's' : ''} uploaded for this stage
              </span>
            </div>
          )}

          {/* Items list */}
          <div className="space-y-2.5">
            {selectedItems.map(({ item, task, images, itemOutsourceJobs }) => (
              <StageItemRow
                key={item.id}
                item={item}
                task={task}
                images={images}
                onImageClick={(img, all) => setLightbox({ img, all })}
                outsourceJobs={itemOutsourceJobs}
              />
            ))}
          </div>

          {/* Empty state */}
          {selectedItems.length === 0 && (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
              <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400">No items found for this stage</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
